import { Leaf, Heart, Award, Sun } from "lucide-react";

const VALUES = [
  {
    icon: Leaf,
    title: "All Natural",
    desc: "Every bottle contains only pure, cold-pressed juice from the freshest produce. We never add water, sugar, or preservatives.",
  },
  {
    icon: Heart,
    title: "Made with Care",
    desc: "We carefully select each ingredient for quality and flavour. Our juices are pressed in small batches to ensure perfection.",
  },
  {
    icon: Award,
    title: "Qatar Compliant",
    desc: "Following Qatar food safety regulations, all our juices are crafted for same-day consumption to guarantee freshness.",
  },
  {
    icon: Sun,
    title: "Daily Wellness",
    desc: "Our wellness shots and juices are designed to support your daily health goals with nature's best nutrients.",
  },
];

export default function About() {
  return (
    <main data-testid="about-page" style={{ background: "#FCF8F6" }}>
      {/* Hero */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-28">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <p
                className="text-xs uppercase tracking-[0.25em] font-semibold mb-6"
                style={{
                  fontFamily: "'Manrope', sans-serif",
                  color: "#A3B18A",
                }}
              >
                About Us
              </p>
              <h1
                className="text-5xl sm:text-6xl tracking-tighter leading-none font-medium mb-8"
                style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  color: "#283618",
                }}
              >
                Our Story
              </h1>
              <div className="space-y-5">
                <p
                  className="text-base md:text-lg leading-relaxed"
                  style={{
                    fontFamily: "'Manrope', sans-serif",
                    color: "#6F5E58",
                  }}
                >
                  At The Zumo Company, we started with a simple belief - <em>juice should be just juice.</em>
                </p>
                <p
                  className="text-base md:text-lg leading-relaxed"
                  style={{
                    fontFamily: "'Manrope', sans-serif",
                    color: "#6F5E58",
                  }}
                >
                  In a world where most drinks are diluted with added sugars, water and preservatives, we chose a different path. One that's honest, clean and uncompromising.
                </p>
                <p
                  className="text-base md:text-lg leading-relaxed"
                  style={{
                    fontFamily: "'Manrope', sans-serif",
                    color: "#6F5E58",
                  }}
                >
                  Based in Doha, every bottle we serve is freshly pressed only after you place your order, using carefully selected fruits and vegetables. Nothing added, nothing taken away - just pure goodness.
                </p>
                <p
                  className="text-base md:text-lg leading-relaxed"
                  style={{
                    fontFamily: "'Manrope', sans-serif",
                    color: "#6F5E58",
                  }}
                >
                  Our juices are crafted for same-day consumption, not only because they taste better, but because freshness isn't a feature for us - it's a standard.
                </p>
                <p
                  className="text-base md:text-lg leading-relaxed"
                  style={{
                    fontFamily: "'Manrope', sans-serif",
                    color: "#6F5E58",
                  }}
                >
                  At Zumo, what you see is exactly what you get: real ingredients, real flavor and a commitment to doing things the right way.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="rounded-3xl overflow-hidden aspect-[4/5]">
                <img
                  src="https://customer-assets.emergentagent.com/job_zumo-fresh-juice/artifacts/s4z8gu17_WhatsApp%20Image%202026-04-15%20at%2010.30.03%20PM.jpeg"
                  alt="Fresh green apples"
                  className="w-full h-full object-cover"
                  data-testid="about-hero-image"
                />
              </div>
              {/* Accent block */}
              <div
                className="absolute -bottom-6 -left-6 w-32 h-32 rounded-3xl -z-10"
                style={{ background: "#F0B7A4" }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section
        className="py-24 md:py-32"
        style={{ background: "#F5D5CB" }}
        data-testid="about-values-section"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center mb-16 md:mb-20">
            <p
              className="text-xs uppercase tracking-[0.25em] font-semibold mb-4"
              style={{
                fontFamily: "'Manrope', sans-serif",
                color: "#6F5E58",
              }}
            >
              What We Stand For
            </p>
            <h2
              className="text-4xl md:text-5xl tracking-tight leading-tight font-medium"
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                color: "#283618",
              }}
            >
              Our Values
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-10 md:gap-12">
            {VALUES.map((val, i) => (
              <div
                key={val.title}
                data-testid={`about-value-${i}`}
                className="flex gap-5"
              >
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
                  style={{ background: "rgba(40, 54, 24, 0.08)" }}
                >
                  <val.icon size={22} color="#283618" />
                </div>
                <div>
                  <h3
                    className="text-xl font-medium mb-2"
                    style={{
                      fontFamily: "'Cormorant Garamond', serif",
                      color: "#2A2A2A",
                    }}
                  >
                    {val.title}
                  </h3>
                  <p
                    className="text-sm leading-relaxed"
                    style={{
                      fontFamily: "'Manrope', sans-serif",
                      color: "#6F5E58",
                    }}
                  >
                    {val.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section
        className="py-24 md:py-32"
        style={{ background: "#FCF8F6" }}
        data-testid="about-process-section"
      >
        <div className="max-w-5xl mx-auto px-6 md:px-10 text-center">
          <p
            className="text-xs uppercase tracking-[0.25em] font-semibold mb-4"
            style={{
              fontFamily: "'Manrope', sans-serif",
              color: "#A3B18A",
            }}
          >
            Our Process
          </p>
          <h2
            className="text-4xl md:text-5xl tracking-tight leading-tight font-medium mb-16"
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              color: "#283618",
            }}
          >
            From Farm to Bottle
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {[
              {
                step: "01",
                title: "Source",
                desc: "We hand-pick the freshest fruits and vegetables from trusted suppliers every morning.",
              },
              {
                step: "02",
                title: "Press",
                desc: "Cold-pressed at low temperatures to retain maximum nutrients, enzymes, and flavour.",
              },
              {
                step: "03",
                title: "Deliver",
                desc: "Bottled and delivered the same day. No storage, no shelf life tricks. Just fresh juice.",
              },
            ].map((item, i) => (
              <div key={item.step} data-testid={`process-step-${i}`}>
                <span
                  className="text-6xl font-medium block mb-4"
                  style={{
                    fontFamily: "'Cormorant Garamond', serif",
                    color: "#F0B7A4",
                  }}
                >
                  {item.step}
                </span>
                <h3
                  className="text-2xl font-medium mb-3"
                  style={{
                    fontFamily: "'Cormorant Garamond', serif",
                    color: "#2A2A2A",
                  }}
                >
                  {item.title}
                </h3>
                <p
                  className="text-sm leading-relaxed"
                  style={{
                    fontFamily: "'Manrope', sans-serif",
                    color: "#6F5E58",
                  }}
                >
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
